import { useApolloClient } from '@apollo/react-hooks';
import useAuthQuery from '../../hooks/useAuthQuery';
import useSelectedAccountParams from '../../hooks/useSelectedAccountParams';
import {
  getTimeOfDayEnrollmnentStatus,
  getTimeOfDayInfo,
  submitTimeOfDayEnrollment,
  submitTimeOfDayUnenrollment,
} from '../../queries/timeOfDay.query';
import {
  AccountDetail,
  PremiseInfo,
  SaStatus,
  TimeOfDayEnrollmentConfirmation,
  TimeOfDayEnrollmentDetails,
  TimeOfDayUnenrollmentConfirmation,
} from '../../__generated__/pge-types';
import { getActiveEncryptedSaId } from '../my-programs/utils';
import { getTimeOfDayEnrollmentDetailsQuery } from '../my-programs/queries';

export default () => {
  const apolloClient = useApolloClient();
  const {
    loading: accountParamsLoading,
    accountParams,
    encryptedAccountNumber
  } = useSelectedAccountParams();

  const { data, loading: timeOfDayLoading } = useAuthQuery<{
    getAccountDetails: Array<AccountDetail>;
  }>(getTimeOfDayInfo, {
    variables: {
      params: {
        accountNumberList: [accountParams],
      },
    },
    errorPolicy: 'all',
    skip: !accountParams,
  });

  const timeOfDayInfo = data?.getAccountDetails?.[0].timeOfDayInfo;
  const encryptedServiceAgreementId = getActiveEncryptedSaId(
      data?.getAccountDetails?.[0]?.premiseInfo as PremiseInfo[] | undefined,
      [SaStatus.Active,SaStatus.Pendingstart]
    );

  //Time of Day
    const {
      data: timeOfDayData,
      loading: timeOfDayEnrollmentLoading,
      error: timeOfDayError,
    } = useAuthQuery<{
      getTimeOfDayEnrollmentDetails: TimeOfDayEnrollmentDetails;
    }>(getTimeOfDayEnrollmentDetailsQuery, {
      variables: {
        params: {
          encryptedServiceAgreementId,
          encryptedAccountNumber,
        },
      },
      skip: !encryptedServiceAgreementId || !encryptedAccountNumber,
    });
  
  async function submitEnrollment() {
    const { data: submitData, errors } = await apolloClient.mutate<{
      submitTimeOfDayEnrollment: TimeOfDayEnrollmentConfirmation;
    }>({
      mutation: submitTimeOfDayEnrollment,
      variables: { payload: accountParams },
      errorPolicy: 'all',
      refetchQueries: [
        {
          query: getTimeOfDayEnrollmnentStatus,
          variables: {
            params: {
              accountNumberList: [accountParams],
            },
          },
        },
      ],
      awaitRefetchQueries: true,
    });

    if (errors && errors.length > 0) {
      return Promise.reject('Mutation failed');
    }

    return submitData?.submitTimeOfDayEnrollment;
  }

  async function submitUnenrollment(unEnrollmentReasonCode: string) {
    const payload = {
      ...accountParams,
      unEnrollmentReasonCd: unEnrollmentReasonCode,
    };

    const { data: submitData, errors } = await apolloClient.mutate<{
      submitTimeOfDayUnenrollment: TimeOfDayUnenrollmentConfirmation;
    }>({
      mutation: submitTimeOfDayUnenrollment,
      variables: { payload },
      errorPolicy: 'all',
      refetchQueries: [
        {
          query: getTimeOfDayEnrollmnentStatus,
          variables: {
            params: {
              accountNumberList: [accountParams],
            },
          },
        },
      ],
      awaitRefetchQueries: true,
    });

    if (errors && errors.length > 0) {
      return Promise.reject('Mutation failed');
    }

    return submitData?.submitTimeOfDayUnenrollment;
  }

  return {
    loading: accountParamsLoading || timeOfDayLoading || timeOfDayEnrollmentLoading,
    timeOfDayInfo,
    timeOfDayData: timeOfDayData?.getTimeOfDayEnrollmentDetails,
    submitEnrollment,
    submitUnenrollment,
  };
};
